import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {APP_STORAGE, IQAPI_HTTP_REQUEST_TIMEOUT, IQAPI_ROUTES} from '../../../environments/environment';
import {ErrorHandlingService} from '../../provider/error-handling/error-handling.service';
import {UserService} from '../../provider/user/user.service';
import {IQApiService} from '../../provider/iqapi/iqapi.service';
import {StorageService} from '../../provider/storage/storage.service';
import {Router} from '@angular/router';
import {concatMap, tap, timeout} from 'rxjs/operators';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {ConfigurationObject, IQAPIVersionObject} from '../../interfaces/interfaces.regisafe';
import {AppConfig} from '../../app.config';
import {Capacitor} from '@capacitor/core';
import {AlertController, ModalController} from '@ionic/angular';
import {
	AvailableResult,
	BiometryType,
	Credentials,
	NativeBiometric,
	SetCredentialOptions
} from 'capacitor-native-biometric';
import {ALERT_TXT, NATIVES_TXT, TOAST_TXT} from '../../strings/language';
import {App} from '@capacitor/app';
import {ConfigModalComponent} from './config-modal/config-modal.component';
import {DateiSystemService} from '../../provider/datei-system/datei-system.service';


@Component({
	selector: 'app-anmeldung',
	templateUrl: './anmeldung.page.html',
	styleUrls: ['./anmeldung.page.scss'],
})
export class AnmeldungPage implements OnInit {

	@ViewChild('fileUpload', {static: false}) fileUpload: ElementRef;

	public configuration: ConfigurationObject;
	public isWeb = false;
	public isDemo = false;
	public isDevelop = false;
	public scanActiv = false;
	public biometricAvailable = false;
	public biometricCredentialsSet = false;
	public biometricLoginInactiv = false;
	public biometricType: BiometryType;
	public alert: HTMLIonAlertElement = null;
	public formInput: null;

	public appInfo: any = {
		version: 'k.A.',
		build: 'k.A.'
	};

	loginFormGroup: FormGroup;
	public loginFormModel = [
		{
			type: 'text',
			label: 'Benutzername',
			id: 'username',
			required: true,
			value: '',
			errorMsg: 'Bitte geben Sie einen Benutzernamen ein.',
		},
		{
			type: 'password',
			label: 'Passwort',
			id: 'password',
			required: true,
			value: '',
			errorMsg: 'Bitte geben Sie ein Passwort ein.'
		}
	];

	modal: HTMLIonModalElement;

	datenschutzlink = 'https://www.regisafe.de/datenschutzerklaerung/';
	kontaktlink = 'support@regisafe.de';

	constructor(private errorHandling: ErrorHandlingService,
				private modalCtrl: ModalController,
				private alertCtrl: AlertController,
				private userService: UserService,
				private iqAPI: IQApiService,
				private storageService: StorageService,
				private router: Router,
				private httpClient: HttpClient,
				public appConfig: AppConfig) {

		const platform = Capacitor.getPlatform();
		this.isWeb = platform === 'web';

		this.configuration = this.appConfig.getConfig();

		const formGroup = {};
		this.loginFormModel.forEach((item) => {
			formGroup[item.id] = new FormControl(item.value, [Validators.required]);
		});
		this.loginFormGroup = new FormGroup(formGroup);

		App.getInfo()
			.then((data) => {
				this.appInfo = data;
			})
			.catch((e) => {
				// be quiet
			});
	}

	ngOnInit() {
		this.checkStoredTokenAndValidate();
		this.checkConfigurationSettings();
	}

	async ionViewDidEnter() {
		this.biometricAvailableCheck();
	}

	/**
	 * loginAndWriteUserdataAndConditions()
	 *
	 * Führt sequenziel zwei Anfragen an die IQAPI aus.
	 * 1. Session.Login
	 * 2. Orgamanager.GetConditions
	 * Nach erfolgreichem Login werden Benutzername und SessionToken in der Datenbank abgelegt.
	 * Danach werden die aktuellen Conditions abgefragt und ebenfalls in der Datenbank abgelegt.
	 * Wurden beide Abfragen erfolgreich ausgeführt wird zur Suche weitergeleitet
	 * Im Fehlerfall wird die entsprechende Fehlermeldung zurückgegeben (HTTP Status bzw. Fehlermeldung von regisafe)
	 */
	public loginAndWriteUserdataAndConditions(storedAccountData?: Credentials) {
		this.userService.presentLoader();

		// Userdaten aus LoginFormular
		let accountData = {
			apiID: this.configuration.iqapiAppId,
			loginID: this.loginFormGroup.value.username,
			password: this.loginFormGroup.value.password
		};

		// Userdaten aus Biometric Login
		if (storedAccountData) {
			accountData = {
				apiID: this.configuration.iqapiAppId,
				loginID: storedAccountData.username,
				password: storedAccountData.password
			};
		}

		// Array der Route
		const urlChain = [
			this.configuration.iqapiEndpoint + IQAPI_ROUTES.login,
			this.configuration.iqapiEndpoint + IQAPI_ROUTES.conditions,
			'assets/static-data/app.searchforms.json',
			'assets/static-data/app.sorting.json',
			'assets/static-data/app.searchwordmode.json'
		];
		const options: object = {headers: {}, responseType: 'json', reportProgress: true, observe: 'response'};
		let versionInConditions: IQAPIVersionObject;
		this.httpClient
			.post(urlChain[0], accountData, options)
			.pipe(
				tap((res: HttpResponse<any>) => {
					// Benutzername und SessionToken in Datenbank speichern
					this.storageService.setUsername(res.body.userName);
					this.storageService.setToken(res.body.sessionToken);
				}),
				concatMap((res: HttpResponse<any>) => this.httpClient.post(urlChain[1],
					{apiId: this.configuration.iqapiAppId, sessionToken: res.body.sessionToken}, options)),
				tap((res: HttpResponse<any>) => {
					// Conditions in Datenbank speichern
					this.storageService.setConditions(res.body);
					versionInConditions = res.body.version;
				}),
				concatMap((res: HttpResponse<any>) => this.httpClient.get(urlChain[2])),
				tap((res: HttpResponse<any>) => {
					// Suchformulare in datenbank speichern
					this.storageService.setSearchFormData(res);
				}),
				concatMap((res: HttpResponse<any>) => this.httpClient.get(urlChain[3])),
				tap((res: HttpResponse<any>) => {
					// Suchformulare in datenbank speichern
					this.storageService.setSortingData(res);
				}),
				concatMap((res: HttpResponse<any>) => this.httpClient.get(urlChain[4])),
				tap((res: HttpResponse<any>) => {
					// Suchformulare in datenbank speichern
					this.storageService.setSearchWordModeData(res);
				}),
				timeout(IQAPI_HTTP_REQUEST_TIMEOUT)
			)
			.subscribe((data: HttpResponse<any>) => {

				const updateNecessary = this.storageService.checkUpdateNecessary(versionInConditions);

				if (updateNecessary) {
					this.storageService.setToken('');
					this.storageService.setConditions({});

					// eslint-disable-next-line max-len
					this.errorHandling.errorMessage('Bitte wenden Sie sich an Ihren Administrator. Ein regisafe-Update muss durchgeführt werden.', 'alert', false);

					this.userService.dismissLoader();
					return false;
				}

				// Weiterleitung zur Suche
				setTimeout(() => {
					this.router.navigate(['regisafe/suche'])
						.then(async (nav) => {

							this.userService.dismissLoader();

							if (Capacitor.getPlatform() !== 'web') {
								const biometriclogin = await this.storageService.getItem(APP_STORAGE.user.biometriclogin);
								if (this.biometricAvailable && !this.biometricCredentialsSet && biometriclogin === 0) {
									this.biometricSetCredentialAlert(accountData);
								}
							} else if (this.biometricCredentialsSet && Capacitor.getPlatform() !== 'web') {
								this.storePasswordForOfflineLoginAlert(accountData);
							}
							this.storePasswordForOfflineLoginAlert(accountData);
							const check = await this.userService.checkFilesInProcess();

							// clear Password Field after login
							this.loginFormGroup.controls.password.setValue('');

						})
						.catch((e) => {
							this.userService.dismissLoader();
						});
				}, 100);

			}, (error) => {
				// Fehlerfall bei der Anfrage an die API
				this.userService.dismissLoader();
				this.errorHandling.handleError(error, 'AnmeldungPage -> loginAndWriteUserdataAndConditions');
				// clear Password Field after login
				this.loginFormGroup.controls.password.setValue('');
			});
	}

	/**
	 * checkStoredTokenAndValidate()
	 *
	 * Holt den SessionToken aus der Datenbank und prüft diesen auf Gültigkeit,
	 * indem die Route Orgamanager.GetConditions aufgerufen wird.
	 * Wird ein gültiges Ergebnis zurückgerufen (Status 200), wird auf die Suche weitergleitet.
	 * Im Fehlerfall wird die entsprechende Fehlermeldung zurückgegeben (HTTP Status bzw. Fehlermeldung von regisafe)
	 */
	async checkStoredTokenAndValidate() {

		// sessionToken aus Datenbank holen
		const token = await this.storageService.getTokenFromStorage();
		if (token && token.length !== '') {
			// eslint-disable-next-line max-len
			this.httpClient.post(this.configuration.iqapiEndpoint + IQAPI_ROUTES.conditions, {
				apiId: this.configuration.iqapiAppId,
				sessionToken: token
			})
				.pipe(timeout(IQAPI_HTTP_REQUEST_TIMEOUT))
				.subscribe((data: HttpResponse<any>) => {
					// Weiterleitung zur Suche
					setTimeout(() => {
						this.router.navigate(['regisafe/suche'])
							.then(async () => {
								this.userService.dismissLoader();
								const check = await this.userService.checkFilesInProcess();
							})
							.catch((e) => {
								this.userService.dismissLoader();
							});
					}, 100);
				}, (error) => {
					this.userService.dismissLoader();
					this.biometricAvailableCheckForOpenDialog();
					// this.errorHandling.handleError(error, 'AnmeldungPage -> checkStoredTokenAndValidate');
				});
		} else {
			this.biometricAvailableCheckForOpenDialog();
		}
	}

	loginFormChange(event): void {
		console.log('loginFormChange: ', event);
		this.formInput = this.loginFormGroup.value;
	}

	checkConfigurationSettings() {
		this.isDemo = this.configuration.demo;
		this.isDevelop = this.configuration.develop;
		if (this.isDemo || this.isDevelop) {
			this.loginFormGroup.controls.username.setValue(this.configuration.username);
			this.loginFormGroup.controls.password.setValue(this.configuration.password);
		} else if (!this.isDemo && !this.isDevelop) {
			this.loginFormGroup.controls.username.setValue('');
			this.loginFormGroup.controls.password.setValue('');
		}

		this.formInput = this.loginFormGroup.value;
	}

	onFileSelected(event) {
		console.log('EVENT: ', event);
		try {
			this.errorHandling.toast.dismiss();
		} catch (e) {
			console.log('dismiss error: ', e);
		}

		const file: File = event.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = () => {
				console.log('FileReader: ', reader);
				this.checkConfigurationFile(reader.result);
			};
			reader.readAsText(file);
		} else {
			console.log('ERROR FILE: ', file);
		}
	}

	checkConfigurationFile(config: any) {
		const propErr = [];
		let confOk = false;
		try {
			const appConfig = JSON.parse(config);

			propErr.push(appConfig.hasOwnProperty('demo'));
			propErr.push(appConfig.hasOwnProperty('develop'));
			propErr.push(appConfig.hasOwnProperty('production'));
			propErr.push(appConfig.hasOwnProperty('iqapiAppId'));
			propErr.push(appConfig.hasOwnProperty('iqapiDomain'));
			propErr.push(appConfig.hasOwnProperty('iqapiEndpoint'));

			confOk = propErr.every(elem => elem === true);
			if (!confOk) {
				// eslint-disable-next-line max-len
				this.errorHandling.errorMessage('Bitte prüfen Sie Ihre Konfigurationsdatei.<br />' + config, 'toast', true);
				this.fileUpload.nativeElement.value = '';
			} else {
				this.checkGivenServerConnection(appConfig);
			}

		} catch (e) {
			// eslint-disable-next-line max-len
			this.errorHandling.errorMessage('Bitte prüfen Sie Ihre Konfigurationsdatei.', 'toast', true);
			this.fileUpload.nativeElement.value = '';
		}
	}

	async checkGivenServerConnection(config) {
		this.httpClient.post(config.iqapiEndpoint + IQAPI_ROUTES.conditions, {})
			.pipe(timeout(IQAPI_HTTP_REQUEST_TIMEOUT))
			.subscribe((success) => {
				// führt immer zum Fehlerfall,
				// da lediglich die Erreichbarkeit des Servers geprüft wird
				// ohne Übergabe von Benutzerdaten
			}, (error) => {
				console.log('checkGivenServerConnection error: ', error);
				this.fileUpload.nativeElement.value = '';
				const errorResp = this.errorHandling.handleConfError(error, 'checkGivenServerConnection');
				if (errorResp) {
					// this.errorHandling.errorMessage('Die Konfiguartion wurde aktualisert. Sie können sich jetzt mit Ihren Zugangsdaten anmelden.', 'alert');
					this.alertConfigurationIsSet(config);
				}
			});
	}

	getScanStatus(event) {
		console.log('getScanStatus: ', event);
		this.scanActiv = event;
	}

	async alertConfigurationIsSet(config) {

		this.alert = await this.alertCtrl.create({
			header: ALERT_TXT.header.str00020,
			// eslint-disable-next-line max-len
			subHeader: ALERT_TXT.subHeader.str00040,
			buttons: [{
				text: ALERT_TXT.buttons.str00010,
				role: 'cancel'
			}, {
				text: ALERT_TXT.buttons.str00070,
				handler: () => {
					this.storageService.removeToken();
					this.storageService.removeConditions();
					this.storageService.setAppConfigurationSync(config);
					this.configuration = config;
					this.checkConfigurationSettings();
					setTimeout(() => {
						window.location.assign('/');
					}, 500);
				}
			}],
			backdropDismiss: false
		});

		await this.alert.present();

	}

	/**
	 * Biometric
	 */

	// https://github.com/epicshaggy/capacitor-native-biometric
	// https://javascript.plainenglish.io/ionic-angular-capacitor-with-biometric-id-2d5666ce1410

	async biometricAvailableCheck() {
		if (Capacitor.getPlatform() !== 'web') {
			NativeBiometric.isAvailable()
				.then((result: AvailableResult) => {
					console.log('checkBiometricAvailable', result);
					if (result.isAvailable) {
						this.biometricType = result.biometryType;
						this.biometricAvailable = true;
						NativeBiometric.getCredentials({
							server: this.configuration.iqapiDomain
						})
							.then((credentials: Credentials) => {
								console.log('credentials: ', credentials);
								this.biometricCredentialsSet = true;
							})
							.catch((error) => {
								this.biometricCredentialsSet = false;
								console.log('credentials error: ', error);
							});
					} else {
						this.biometricAvailable = false;
					}

				});
		} else {
			this.biometricAvailable = false;
		}

		// Prüfen ob Benutzer den biometrischen Login in der App deaktiviert hat (2)
		const biometriclogin = await this.storageService.getItem(APP_STORAGE.user.biometriclogin);
		console.log('## biometriclogin ###: ', biometriclogin);
		this.biometricLoginInactiv = biometriclogin === 2;
	}

	async biometricAvailableCheckForOpenDialog() {
		if (Capacitor.getPlatform() !== 'web') {
			NativeBiometric.isAvailable()
				.then((result: AvailableResult) => {
					if (result.isAvailable) {
						NativeBiometric.getCredentials({
							server: this.configuration.iqapiDomain
						})
							.then(async (credentials: Credentials) => {
								console.log('### biometricAvailableCheckForOpenDialog: ', credentials);

								// Prüfen ob Benutzer den biometrischen Login in der App deaktiviert hat (2)
								const biometriclogin = await this.storageService.getItem(APP_STORAGE.user.biometriclogin);
								console.log('### biometricAvailableCheckForOpenDialog iometriclogin: ', biometriclogin);
								if (biometriclogin !== 2) {
									this.biometricLogin();
								}

							})
							.catch((error) => {
								console.log('### biometricAvailableCheckForOpenDialog error: ', error);
							});
					}

				});
		}


	}

	biometricLogin() {
		if (Capacitor.getPlatform() !== 'web') {
			NativeBiometric.isAvailable()
				.then((result: AvailableResult) => {
					if (result.isAvailable) {
						NativeBiometric.getCredentials({
							server: this.configuration.iqapiDomain
						}).then((credentials) => {
							NativeBiometric.verifyIdentity({
								reason: NATIVES_TXT.biometric.reason,
								title: NATIVES_TXT.biometric.title,
								subtitle: NATIVES_TXT.biometric.subtitle,
								description: NATIVES_TXT.biometric.description
							}).then(() => {
								this.loginAndWriteUserdataAndConditions(credentials);
							}).catch((e) => {
								this.errorHandling.errorMessage(TOAST_TXT.message.str00060, 'toast');
							});

						}).catch((error) => {
							this.errorHandling.errorMessage(TOAST_TXT.message.str00070, 'toast');
						});
					}
				});
		}
	}

	biometricSetCredential(credentials: any) {
		if (Capacitor.getPlatform() !== 'web') {
			NativeBiometric.isAvailable().then((result: AvailableResult) => {
				if (result.isAvailable) {

					const options: SetCredentialOptions = {
						username: credentials.loginID,
						password: credentials.password,
						server: this.configuration.iqapiDomain,
					};

					this.storageService.setItem(APP_STORAGE.user.offlinelogin, 1);
					NativeBiometric.setCredentials(options)
						.then()
						.catch((e) => {
							console.log('SetCredentials error: ', e);
						});
				}
			});
		}
	}

	async biometricSetCredentialAlert(accountData) {
		this.alert = await this.alertCtrl.create({
			header: ALERT_TXT.header.str00020,
			subHeader: ALERT_TXT.subHeader.str00050,
			buttons: [{
				text: ALERT_TXT.buttons.str00080,
				handler: () => {
					this.biometricSetCredential(accountData);
				}
			}, {
				text: ALERT_TXT.buttons.str00090,
				handler: () => {
					this.storageService.setItem(APP_STORAGE.user.biometriclogin, 2);
				},
				role: 'cancel'
			}]
		});

		await this.alert.present();

		await this.alert.onDidDismiss().then((data) => {
			this.storePasswordForOfflineLoginAlert(accountData);
		});
	}

	biometricDeleteCredentials() {
		NativeBiometric.deleteCredentials({
			server: this.configuration.iqapiDomain,
		}).then(() => {
			this.biometricCredentialsSet = false;
			window.location.assign('/');
		});
	}

	async setConfig() {
		this.modal = await this.modalCtrl.create({
			component: ConfigModalComponent,
			cssClass: 'config-modal'
		});

		this.modal.present();
	}

	async storePasswordForOfflineLoginAlert(data) {

		const offlinelogin = await this.storageService.getItem(APP_STORAGE.user.offlinelogin);
		if (offlinelogin !== 0) {
			return false;
		}

		this.alert = await this.alertCtrl.create({
			header: ALERT_TXT.header.str00090,
			message: ALERT_TXT.message.str00030,
			buttons: [{
				text: ALERT_TXT.buttons.str00080,
				handler: () => {
					this.userService.storePasswordForOfflineLogin(data);
				}
			}, {
				text: ALERT_TXT.buttons.str00220,
				handler: async () => {
					await this.storageService.setItem(APP_STORAGE.user.offlinelogin, 2);
				},
				role: 'cancel'
			}]
		});

		await this.alert.present();
	}

	// async storePasswordForOfflineLogin(data) {
	// 	const makeHash = await this.makeHash(data.password);
	// 	console.log('storePasswordForOfflineLogin makeHash: ', makeHash);
	// 	if (makeHash) {
	// 		await this.storageService.setItem(APP_STORAGE.user.offlinelogin, 1);
	// 	} else {
	// 		this.errorHandling.errorMessage(TOAST_TXT.message.str00040, 'toast', true);
	// 	}
	// }
	//
	// async makeHash(input: string = 'secretpassword'): Promise<boolean> {
	// 	const reversed = this.reverseString(input.trim());
	// 	console.log('reversed: ', reversed);
	// 	const md5Hash = Md5.hashStr(reversed);
	// 	console.log('md5Hash: ', md5Hash);
	// 	const hash = CryptoJS.SHA3(md5Hash, {outputLength: 512});
	// 	console.log('CryptoJS.SHA3: ', hash.toString());
	// 	const stored = await this.storeHash(hash.toString());
	// 	return stored;
	// }
	//
	// reverseString(str: string): string {
	// 	const splitted = str.split('');
	// 	const reversedArray = splitted.reverse();
	// 	return reversedArray.join('');
	// }
	//
	// async storeHash(hash: string): Promise<any> {
	// 	return this.dateiService.schreibeDateiHash(hash);
	// }

}
